/**
 * BgImagesPanel.tsx
 *
 * Shown as a third tab in the taskpane when the active template is
 * "asset-allocation" (or any template whose profile sets
 * `features.heroBgImages = true`).
 *
 * Layout:
 *   [BG Images]                   ← tab label (shown in App.tsx)
 *   ┌─────────────────────────┐
 *   │ 🖼 Mountains             │  ← selected card (highlighted)
 *   │ [thumbnail]              │
 *   └─────────────────────────┘
 *   ┌─────────────────────────┐
 *   │ 🖼 Ocean                 │
 *   │ [thumbnail]              │
 *   └─────────────────────────┘
 *   [ Apply Image ]             ← CTA button
 */

import React, { useState, useCallback } from "react";
import { makeStyles, tokens, Spinner, Button, Text } from "@fluentui/react-components";
import { ImageRegular, CheckmarkCircleRegular, WarningRegular } from "@fluentui/react-icons";
import useBgImages from "../hooks/useBgImages.hook";
import applyHeroBgImage from "../../word/document/applyHeroBgImage";
import { useActiveTemplate } from "../hooks/useActiveTemplate";
import { logger } from "../../core/logging/index";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
  },
  sectionLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingVerticalS,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusLarge,
    border: `1.5px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    backgroundColor: tokens.colorNeutralBackground1,
    transition: "border-color 0.15s, background-color 0.15s",
  },
  cardSelected: {
    border: `1.5px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  thumbnail: {
    width: "100%",
    aspectRatio: "16/9",
    objectFit: "cover",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  thumbnailPlaceholder: {
    width: "100%",
    aspectRatio: "16/9",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorNeutralForeground3,
    fontSize: "24px",
  },
  cardLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  applyRow: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalS,
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
  },
  statusSuccess: {
    color: tokens.colorStatusSuccessForeground1,
  },
  statusError: {
    color: tokens.colorStatusDangerForeground1,
  },
  emptyState: {
    padding: tokens.spacingVerticalL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
});

export const BgImagesPanel: React.FC = () => {
  const styles = useStyles();
  const { images, selectedId, select } = useBgImages();
  const { profile } = useActiveTemplate();

  const [isApplying, setIsApplying] = useState(false);
  const [status, setStatus] = useState<
    { type: "success" | "error"; message: string } | null
  >(null);

  const handleApply = useCallback(async () => {
    if (!selectedId) return;

    const selected = images.find((img) => img.id === selectedId);
    if (!selected) return;

    setIsApplying(true);
    setStatus(null);

    try {
      // Convert the image URL to base64
      const response = await fetch(selected.url);
      const blob = await response.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const result = await applyHeroBgImage(base64);

      if (result.success) {
        setStatus({ type: "success", message: `"${selected.label}" applied to hero section` });
        logger.info("BG image applied", { imageId: selectedId, template: profile.id });
      } else {
        setStatus({
          type: "error",
          message: "message" in result ? result.message : "Failed to apply image.",
        });
      }
    } catch (err: any) {
      setStatus({
        type: "error",
        message: err?.message ?? "Failed to apply image. Please try again.",
      });
      logger.error("BG image apply failed", { imageId: selectedId, err });
    } finally {
      setIsApplying(false);
    }
  }, [selectedId, images, profile.id]);

  if (images.length === 0) {
    return (
      <div className={styles.root}>
        <p className={styles.emptyState}>
          No background images found.
          <br />
          Add images to <code>assets/bg-images/</code> and rebuild.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Text className={styles.sectionLabel}>Hero background</Text>

      <div className={styles.grid}>
        {images.map((img) => (
          <div
            key={img.id}
            className={`${styles.card} ${selectedId === img.id ? styles.cardSelected : ""}`}
            onClick={() => select(img.id)}
            role="radio"
            aria-checked={selectedId === img.id}
            aria-label={img.label}
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && select(img.id)}
          >
            {img.url ? (
              <img
                src={(img as any).thumbnailUrl ?? img.url}
                alt={img.label}
                className={styles.thumbnail}
                loading="lazy"
              />
            ) : (
              <div className={styles.thumbnailPlaceholder} aria-hidden="true">
                <ImageRegular />
              </div>
            )}
            <span className={styles.cardLabel}>{img.label}</span>
          </div>
        ))}
      </div>

      <div className={styles.applyRow}>
        <Button
          appearance="primary"
          onClick={handleApply}
          disabled={!selectedId || isApplying}
          icon={isApplying ? <Spinner size="tiny" /> : undefined}
          aria-label={isApplying ? "Applying image…" : "Apply selected background image"}
        >
          {isApplying ? "Applying…" : "Apply Image"}
        </Button>

        {status && (
          <div
            className={`${styles.statusRow} ${
              status.type === "success" ? styles.statusSuccess : styles.statusError
            }`}
            role={status.type === "error" ? "alert" : "status"}
          >
            {status.type === "success" ? (
              <CheckmarkCircleRegular aria-hidden="true" />
            ) : (
              <WarningRegular aria-hidden="true" />
            )}
            <span>{status.message}</span>
          </div>
        )}
      </div>
    </div>
  );
};